import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, Box, Typography, Avatar, IconButton, Button, TextField, CircularProgress } from '@mui/material';
import { Close, EventNoteOutlined } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import { employeeTermination } from 'utils/API/Mis Apis/HrPortelApis';

const DeactivationModal = ({ open, handleClose, employee, onRefresh }) => {
    const [dateOfLeaving, setDateOfLeaving] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const roleColor = '#d32f2f'; // Red color for deactivation context

    useEffect(() => {
        if (open) {
            setDateOfLeaving(''); // Reset date each time it opens
        }
    }, [open, employee]);

    const handleDeactivate = async () => {
        if (!dateOfLeaving) {
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                employee_id: employee.id,
                date_of_leaving: dateOfLeaving
            };
            const response = await employeeTermination(payload);
            if (response?.data?.success) {
                globalNotifySuccess(response.data.message || 'Employee deactivated successfully');
                if (onRefresh) onRefresh();
                handleClose();
            }
        } catch (error) {
            handleCatchError(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!employee) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '90vh'
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        backdropFilter: 'blur(4px)'
                    }
                }
            }}
        >
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.3 }}
                        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
                    >
                        {/* Header (Same style as PortalAssignModal but red theme) */}
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, #7f1d1d 0%, #d32f2f 60%, #ef4444 100%)',
                                px: 3,
                                py: 2.5,
                                position: 'relative',
                                color: '#fff',
                                overflow: 'hidden'
                            }}
                        >
                            {/* Decorative circles */}
                            {[
                                { size: 100, top: -40, right: -20, op: 0.1 },
                                { size: 60, top: 20, right: 60, op: 0.05 }
                            ].map((c, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        position: 'absolute',
                                        top: c.top,
                                        right: c.right,
                                        width: c.size,
                                        height: c.size,
                                        borderRadius: '50%',
                                        bgcolor: '#fff',
                                        opacity: c.op,
                                        pointerEvents: 'none'
                                    }}
                                />
                            ))}

                            <IconButton
                                onClick={handleClose}
                                size="small"
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    color: 'rgba(255,255,255,0.7)',
                                    zIndex: 2,
                                    '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        fontSize: 24,
                                        fontWeight: 800,
                                        bgcolor: '#fff',
                                        color: roleColor,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        border: '2px solid rgba(255,255,255,0.2)'
                                    }}
                                >
                                    {employee.name.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', letterSpacing: '-0.01em' }}>
                                        {employee.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            opacity: 0.85,
                                            fontWeight: 600,
                                            color: '#fff',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            fontSize: '0.65rem'
                                        }}
                                    >
                                        {employee.role?.replace('_', ' ')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" color="text.secondary" fontWeight={500} sx={{ mb: 2 }}>
                                    Please provide the date of leaving to proceed with the deactivation process.
                                    <span style={{ color: 'red', fontWeight: 600 }}> This action cannot be undone.</span>
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, px: 0.5 }}>
                                    <EventNoteOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                                        Date of Leaving
                                    </Typography>
                                </Box>

                                <TextField
                                    fullWidth
                                    type="date"
                                    size="small"
                                    value={dateOfLeaving}
                                    onChange={(e) => setDateOfLeaving(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        sx: {
                                            fontSize: '0.85rem',
                                            borderRadius: 2,
                                            bgcolor: '#fff',
                                            '& fieldset': { borderColor: 'divider' },
                                            '&:hover fieldset': { borderColor: 'error.light' },
                                            '&.Mui-focused fieldset': { borderColor: 'error.main', borderWidth: 2 }
                                        }
                                    }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                color="error"
                                onClick={handleDeactivate}
                                disabled={isLoading || !dateOfLeaving}
                                sx={{
                                    mt: 'auto',
                                    borderRadius: 3,
                                    py: 1.2,
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    bgcolor: '#d32f2f',
                                    boxShadow: '0 8px 16px rgba(211, 47, 47, 0.25)',
                                    '&:hover': {
                                        bgcolor: '#b71c1c',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 20px rgba(211, 47, 47, 0.35)'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Deactivation'}
                            </Button>
                        </DialogContent>
                    </motion.div>
                )}
            </AnimatePresence>
        </Dialog>
    );
};

DeactivationModal.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    employee: PropTypes.object,
    onRefresh: PropTypes.func
};

export default DeactivationModal;
